import { DynamoDBClient, QueryCommand } from "@aws-sdk/client-dynamodb";
import { unmarshall } from "@aws-sdk/util-dynamodb";
import { useQuery } from "@tanstack/vue-query";
import { ComputedRef } from "vue";
import { Camera } from "../../lib/dynamodb/types";

/**
 * Get all cameras from GSI_all_entity
 */
export async function getAvailableCameras(dynamodb: {
	user_table_name: string;
	client: DynamoDBClient;
}): Promise<Camera[]> {
	const { client, user_table_name } = dynamodb;

	if (!client) {
		throw new Error("[DynamoDB] Camera query failed: DynamoDB client is not initialized");
	}

	if (!user_table_name) {
		throw new Error("[DynamoDB] Camera query failed: user_table_name is not set");
	}

	const cameras: Camera[] = [];

	const query = {
		TableName: user_table_name,
		IndexName: "GSI_by___en",
		KeyConditionExpression: "#en = :entityType",
		ProjectionExpression: "PK, created_at, updated_at, #desc",
		ExpressionAttributeNames: {
			"#en": "__en",
			"#desc": "description",
		},
		ExpressionAttributeValues: {
			":entityType": { S: "CAM" },
		},
	};

	const command = new QueryCommand(query);

	try {
		const result = await client.send(command);
		if (result.Items) {
			for (const item of result.Items) {
				const unmarshalled = unmarshall(item);
				const pk = unmarshalled.PK as string;
				const cameraId = pk.replace("CAM#", "");
				cameras.push({
					cam_id: cameraId,
					description: unmarshalled.description || "",
					created_at: unmarshalled.created_at || 0,
					updated_at: unmarshalled.updated_at || 0,
				});
			}
		}
	} catch (err) {
		throw err;
	}

	return cameras;
}

export function useAvailableCameras(
	dynamodb: ComputedRef<{
		user_table_name: string;
		cam_table_name?: string;
		client: DynamoDBClient;
	} | null>,
	credentialsReset: ComputedRef<number>,
) {
	return useQuery({
		queryKey: ["available-cameras", credentialsReset],
		queryFn: () =>
			getAvailableCameras(
				dynamodb.value as {
					user_table_name: string;
					client: DynamoDBClient;
				},
			),
		enabled: () => !!dynamodb.value,
	});
}
